// Session 63/64: the brand block everybody fills in (colours, logo, font),
// the four looks for the title image we make for a post, and the Blog images
// setting (we make it or the client gives photos, the look, notes for the
// painter).
//
//   BrandSetup   two colours, a headline font and a logo. Under Edit client >
//                Voice and brand, and the "Your logo and colours" onboarding
//                step. The newsletter and the title images both read it.
//   LookPicker   four tiles with a small CSS sketch of each layout
//   ImagesSetup  the Blog images section, shared by onboarding and Edit client
//
// The painter (which image model paints the scene) is attached to the look;
// only Edit client can override it, and clients never see model names.
import { useEffect, useState } from 'react';
import { supabase } from '../supabase.js';
import { Field } from './FormBits.jsx';

export const HERO_LOOKS = [
  ['editorial', 'Editorial',
   'Your title on a soft panel at the left, the picture filling the right. Calm and premium.'],
  ['scene', 'Scene',
   'The picture fills the frame, your title sits low over a soft shadow. Bold and photographic.'],
  ['crisp', 'Crisp',
   'A solid block in your main colour at the left with the title, the picture at the right. Clean and modern.'],
  ['card', 'Card',
   'No picture: a flat card in your colours with the title and your logo. Simple, and always the same.'],
];

export const PAINTERS = [
  ['', 'As the look uses (Editorial and Scene paint with ChatGPT, Crisp with Google)'],
  ['openai', 'ChatGPT'],
  ['gemini', 'Google'],
  ['none', 'No painted scene: the flat card every time'],
];

const FONTS = [['', 'Clean sans-serif (default)'], ['georgia', 'Georgia (serif)'], ['arial', 'Arial'],
               ['trebuchet', 'Trebuchet'], ['verdana', 'Verdana']];

export function brandFromPack(brand, newsletter) {
  const b = brand || {};
  const n = newsletter || {};
  return {
    main_color: b.main_color || n.main_color || '#1A7B88',
    accent_color: b.accent_color || n.accent_color || '#DC5F13',
    headline_font: b.headline_font ?? n.headline_font ?? '',
    logo_path: b.logo_path || n.logo_path || '',
  };
}

export function brandPayload(v) {
  const { logo_preview_url, ...rest } = v || {};
  return rest;
}

// Session 64: the old 'either' reads as 'generated'; nothing set reads as 'client'.
export function imagesFromPack(images) {
  const p = images || {};
  const source = p.source === 'either' ? 'generated' : (p.source || 'client');
  return {
    max_in_body: p.max_in_body ?? 2,
    source,
    look: p.look || 'editorial',
    painter: p.painter || '',
    notes: p.notes || '',
  };
}

/* A tiny sketch of each layout, drawn with divs in the chosen colours. */
function Sketch({ look, main, accent }) {
  const style = { '--lk-main': main || '#1A7B88', '--lk-accent': accent || '#DC5F13' };
  return (
    <div className={`hero-sketch hero-sketch-${look}`} style={style} aria-hidden="true">
      <div className="hero-sketch-photo" />
      <div className="hero-sketch-panel">
        <i /><b /><b className="short" /><em />
      </div>
    </div>
  );
}

export function LookPicker({ value, onChange, main, accent, compact = false }) {
  return (
    <div className={`hero-looks${compact ? ' hero-looks-compact' : ''}`}>
      {HERO_LOOKS.map(([key, name, why]) => (
        <label key={key} className={`hero-look${value === key ? ' hero-look-on' : ''}`}>
          <input type="radio" name="hero_look" value={key} checked={value === key} onChange={() => onChange(key)} />
          <Sketch look={key} main={main} accent={accent} />
          <span className="hero-look-name">{name}</span>
          {!compact && <span className="hero-look-why">{why}</span>}
        </label>
      ))}
    </div>
  );
}

const SOURCES = [
  ['generated', 'Make the title image for me',
   'We make a clean title image for every post: a picture in the style you pick below, with your title and logo on it in your colours. Nothing to do on your side. You can still replace any of them with your own photo.'],
  ['client', 'I will give you photos',
   'You add the picture for the top of each post yourself. Best for a business whose own work is the point: design, photography, food, places.'],
];

export function ImagesSetup({ value, onChange, brand, agency = false, compact = false }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  return (
    <div className="images-setup">
      <div className="onb-choices">
        {SOURCES.map(([key, name, why]) => (
          <label key={key} className={`onb-choice${v.source === key ? ' onb-choice-on' : ''}`}>
            <input type="radio" name="image_source" value={key} checked={v.source === key} onChange={() => set({ source: key })} />
            <span>
              <span className="onb-choice-name">{name}</span>
              <span className="onb-choice-why">{why}</span>
            </span>
          </label>
        ))}
      </div>
      {v.source === 'generated' && (
        <>
          <p className="hint">The look. Your title and logo go on it in your colours{agency ? '' : ' (from the logo and colours you gave us)'}.</p>
          <LookPicker value={v.look || 'editorial'} onChange={(look) => set({ look })}
                      main={brand?.main_color} accent={brand?.accent_color} compact={compact} />
          <label className="field">
            <span className="field-label">Anything we should know for the picture (optional)</span>
            <textarea rows={3} value={v.notes || ''} maxLength={600}
                      placeholder="What your customers should see, what should never appear, the setting you work in, the tools you use…"
                      onChange={(e) => set({ notes: e.target.value })} />
            <span className="field-hint">Read every time a picture is made for one of your posts.</span>
          </label>
          {agency && (
            <Field label="Painter" hint="Which image model paints the scene behind the title. Leave on the look's own unless you want to compare. The flat card costs nothing.">
              <select value={v.painter || ''} onChange={(e) => set({ painter: e.target.value })}>
                {PAINTERS.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
              </select>
            </Field>
          )}
        </>
      )}
    </div>
  );
}

const LOGO_MIME = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif' };
const LOGO_MAX_BYTES = 5 * 1024 * 1024;

export default function BrandSetup({ value, onChange, clientId }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!v.logo_path) { if (v.logo_preview_url) set({ logo_preview_url: '' }); return undefined; }
    (async () => {
      const { data } = await supabase.storage.from('post-images').createSignedUrl(v.logo_path, 600);
      if (!cancelled && data?.signedUrl && data.signedUrl !== v.logo_preview_url) set({ logo_preview_url: data.signedUrl });
    })();
    return () => { cancelled = true; };
  }, [v.logo_path]); // eslint-disable-line react-hooks/exhaustive-deps

  async function uploadLogo(file) {
    if (!file) return;
    setLogoError(null);
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!LOGO_MIME[ext]) { setLogoError('Please use a PNG, JPG, WebP or GIF. A PNG with a transparent background works best.'); return; }
    if (file.size > LOGO_MAX_BYTES) { setLogoError('That file is over 5 MB.'); return; }
    if (!clientId) { setLogoError('No client to save the logo against.'); return; }
    setLogoBusy(true);
    const path = `${clientId}/brand/logo.${ext === 'jpeg' ? 'jpg' : ext}`;
    const { error } = await supabase.storage.from('post-images').upload(path, file, { upsert: true, contentType: LOGO_MIME[ext] });
    if (error) { setLogoError(`Couldn't upload the logo: ${error.message}`); setLogoBusy(false); return; }
    // A signed URL rather than a blob: URL, because the newsletter preview
    // iframe is sandboxed and a blob: URL from this origin would not load in it.
    const { data } = await supabase.storage.from('post-images').createSignedUrl(path, 600);
    setLogoBusy(false);
    onChange({ ...v, logo_path: path, logo_preview_url: data?.signedUrl || '' });
  }

  return (
    <div className="brand-setup">
      <div className="nl-logo">
        <div className="nl-logo-box">
          {v.logo_preview_url
            ? <img src={v.logo_preview_url} alt="Your logo" />
            : <span className="hint" style={{ margin: 0 }}>No logo yet. Without one, your business name is set in your font and colour.</span>}
        </div>
        <div className="nl-logo-actions">
          <input id="brand-logo-file" type="file" accept=".png,.jpg,.jpeg,.webp,.gif,image/png,image/jpeg,image/webp,image/gif"
                 style={{ display: 'none' }} onChange={(e) => uploadLogo(e.target.files?.[0])} />
          <button type="button" className="btn" disabled={logoBusy}
                  onClick={() => document.getElementById('brand-logo-file')?.click()}>
            {logoBusy ? 'Uploading…' : (v.logo_path ? 'Change logo' : 'Upload a logo')}
          </button>
          {v.logo_path && <button type="button" className="link-btn" onClick={() => set({ logo_path: '', logo_preview_url: '' })}>Remove</button>}
          {logoError && <span className="field-hint nl-warn">{logoError}</span>}
          <span className="field-hint">Optional. A PNG with a transparent background looks best. Used on your newsletter and on the title image of each post. PNG, JPG, WebP or GIF.</span>
        </div>
      </div>
      <div className="inline-fields">
        <Field label="Main colour" hint="Headline blocks, buttons and links on the newsletter; the title or the panel on a title image.">
          <div className="colour-field">
            <input type="color" value={/^#[0-9A-F]{6}$/i.test(v.main_color || '') ? v.main_color : '#1A7B88'} onChange={(e) => set({ main_color: e.target.value.toUpperCase() })} />
            <input type="text" value={v.main_color || ''} maxLength={7} onChange={(e) => set({ main_color: e.target.value.toUpperCase() })} placeholder="#1A7B88" />
          </div>
        </Field>
        <Field label="Accent colour" hint="Highlights, the call to action, the rule under a title.">
          <div className="colour-field">
            <input type="color" value={/^#[0-9A-F]{6}$/i.test(v.accent_color || '') ? v.accent_color : '#DC5F13'} onChange={(e) => set({ accent_color: e.target.value.toUpperCase() })} />
            <input type="text" value={v.accent_color || ''} maxLength={7} onChange={(e) => set({ accent_color: e.target.value.toUpperCase() })} placeholder="#DC5F13" />
          </div>
        </Field>
        <Field label="Headline font" hint="Headlines only. All four work in every email tool.">
          <select value={v.headline_font || ''} onChange={(e) => set({ headline_font: e.target.value })}>
            {FONTS.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
          </select>
        </Field>
      </div>
      <p className="hint">Lighter tints are worked out from these, and the text on any coloured block is set dark or white so it always reads.</p>
    </div>
  );
}
